// © 2026 Holmes Computer Consultants. All rights reserved. -- www.holmesconsultants.com
//
// Where a dragged thing actually lands (M9-01, 11 §3.1).
//
// "Snaps to a 1 mm grid, to symmetry about the centre line, and to existing geometry" is three
// rules that can each be right on their own and disagree together, so the whole of this file is
// about **which one wins and how the author knows**.
//
// **Each axis is decided separately.** A device dragged near another device's x and nothing in
// particular in y should line up horizontally and stay where it was put vertically — snapping both
// axes to the same target, which is what a whole-point rule does, drags it bodily onto the other
// device. The exception is a genuine point match: when the pointer is within tolerance of an
// existing anchor in *both* axes, an author is plainly aiming at that point, and landing exactly on
// it is what they meant.
//
// **Precedence is geometry, then symmetry, then grid**, and it is a strict order rather than a
// nearest-wins contest. Nearest-wins sounds fairer and behaves worse: the grid is everywhere, so at
// any zoom where a millimetre is more than a couple of pixels the grid is almost always nearer than
// the alignment an author is reaching for, and alignment becomes unreachable exactly when the view
// is close enough to want it.
//
// **The grid never fails.** It is the last rule and it always answers, so every drag lands on a
// millimetre — an author cannot end up with a flipper at 0.0714237 m because they dragged quickly.
//
// **Every result names the rule that caught it**, because a snap that is invisible is a snap that
// reads as the canvas ignoring the pointer. The canvas draws a centre line when symmetry caught an
// axis and a tick against the device that supplied an alignment, and the inspector shows the number
// that resulted. A snap you can see is a snap you can trust; one you cannot is a bug report.
//
// Pure: no DOM, no canvas, no clock. The caller converts pixels to metres — see
// `viewport.metresPerPixel` — because tolerance is a fingertip and a fingertip is pixels.

import {type EditorPart as MachineDevice,deviceAnchors} from './parts'
import type {  Point2 } from '../../core/ts/machine'
import { } from '../../core/ts/machine'
import { GRID_METRES } from './viewport'

/** Which rule decided an axis. */
export type SnapRule =
  /** An existing device's anchor, in this axis. */
  | 'geometry'
  /** The playfield's centre line, or the mirror of an existing anchor across it. */
  | 'symmetry'
  /** The 1 mm design grid. */
  | 'grid'

/** One axis of an answer: where it landed, why, and what it landed on. */
export interface AxisSnap {
  readonly value: number
  readonly rule: SnapRule
  /**
   * The device that supplied the alignment, for `geometry` and mirrored `symmetry`.
   *
   * Absent for the grid and for the centre line, which belong to the playfield rather than to
   * anything an author placed.
   */
  readonly against?: string
}

export interface SnapResult {
  readonly at: Point2
  readonly x: AxisSnap
  readonly y: AxisSnap
  /** True when both axes landed on the same anchor — a point match rather than two alignments. */
  readonly onPoint: boolean
}

export interface SnapOptions {
  /** Devices to align against. The one being dragged should not be in this list. */
  readonly devices: readonly MachineDevice[]
  /**
   * How near counts, in metres. Convert from pixels with `metresPerPixel` so the catch radius is
   * the same fingertip at every zoom.
   */
  readonly toleranceMetres: number
  /** Off for the author who wants a deliberately asymmetric machine. Symmetry is a help, not a law. */
  readonly symmetry?: boolean
  /** The grid, in metres. 1 mm unless a caller has a reason. */
  readonly gridMetres?: number
}

function onGrid(value: number, grid: number): number {
  if (!(grid > 0)) return value
  const snapped = Math.round(value / grid) * grid
  // Rounded back to the grid's own precision. `Math.round(x / 0.001) * 0.001` produces
  // 0.07200000000000001 for values that are exactly on a millimetre, and a definition full of those
  // is a diff nobody can read and a file that changes when nothing did.
  const places = Math.max(0, Math.ceil(-Math.log10(grid)) + 1)
  return Number(snapped.toFixed(places))
}

/** Every candidate an axis could align to, with what supplied it. */
interface Candidate {
  readonly value: number
  readonly rule: SnapRule
  readonly against?: string
}

function nearest(value: number, candidates: readonly Candidate[], tolerance: number): Candidate | undefined {
  let best: Candidate | undefined
  let bestGap = tolerance
  for (const candidate of candidates) {
    const gap = Math.abs(candidate.value - value)
    // Strictly nearer, so the first candidate offered wins a tie — and the callers below offer them
    // in the order the rules are ranked, which makes ties resolve by precedence rather than by
    // whichever device happened to be earlier in the definition.
    if (gap < bestGap) {
      best = candidate
      bestGap = gap
    }
  }
  return best
}

/**
 * Snap one point.
 *
 * The whole rule set, in one call, so a caller never has to remember the precedence.
 */
export function snapPoint(raw: Point2, options: SnapOptions): SnapResult {
  const grid = options.gridMetres ?? GRID_METRES
  const tolerance = options.toleranceMetres
  const symmetry = options.symmetry !== false

  const xs: Candidate[] = []
  const ys: Candidate[] = []
  // Anchor by anchor, so a point match can be recognised: both axes catching the same device is not
  // the same event as two devices each supplying one.
  const points: { readonly at: Point2; readonly id: string }[] = []

  for (const device of options.devices) {
    for (const anchor of deviceAnchors(device)) {
      points.push({ at: anchor.at, id: device.id })
      xs.push({ value: anchor.at[0], rule: 'geometry', against: device.id })
      ys.push({ value: anchor.at[1], rule: 'geometry', against: device.id })
    }
  }

  if (symmetry) {
    // The centre line first: it is the alignment an author reaches for most and the only one that
    // is a property of the machine rather than of what is already on it.
    xs.push({ value: 0, rule: 'symmetry' })
    for (const point of points) {
      // …and the mirror of everything placed, which is how the second half of a symmetric pair is
      // put down: drag the right slingshot near where the left one's reflection is, and it lands
      // exactly there.
      xs.push({ value: -point.at[0], rule: 'symmetry', against: point.id })
    }
  }

  // A genuine point match: within tolerance in both axes of one anchor. Checked before the axes are
  // decided separately, because an author dragging onto a point means the point.
  let onPoint: { at: Point2; id: string } | undefined
  let bestGap = Number.POSITIVE_INFINITY
  for (const point of points) {
    const dx = Math.abs(point.at[0] - raw[0])
    const dy = Math.abs(point.at[1] - raw[1])
    if (dx > tolerance || dy > tolerance) continue
    const gap = dx * dx + dy * dy
    if (gap < bestGap) {
      bestGap = gap
      onPoint = point
    }
  }
  if (onPoint !== undefined) {
    return {
      at: onPoint.at,
      x: { value: onPoint.at[0], rule: 'geometry', against: onPoint.id },
      y: { value: onPoint.at[1], rule: 'geometry', against: onPoint.id },
      onPoint: true,
    }
  }

  const x = axis(raw[0], xs, tolerance, grid)
  const y = axis(raw[1], ys, tolerance, grid)
  return { at: [x.value, y.value], x, y, onPoint: false }
}

function axis(value: number, candidates: readonly Candidate[], tolerance: number, grid: number): AxisSnap {
  // Geometry, then symmetry — a strict order, so an alignment stays reachable at a zoom where the
  // grid is everywhere. Within one rule, nearest wins.
  const geometry = nearest(value, candidates.filter((c) => c.rule === 'geometry'), tolerance)
  const chosen = geometry ?? nearest(value, candidates.filter((c) => c.rule === 'symmetry'), tolerance)
  if (chosen !== undefined) {
    return chosen.against === undefined
      ? { value: chosen.value, rule: chosen.rule }
      : { value: chosen.value, rule: chosen.rule, against: chosen.against }
  }
  return { value: onGrid(value, grid), rule: 'grid' }
}

